import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../App';
import { 
  Bot, Send, Sparkles, Lightbulb, TrendingUp, Megaphone,
  Image, FileText, RefreshCw, Copy, Check, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const quickPrompts = [
  { icon: Megaphone, label: 'Write ad copy', prompt: 'Write compelling ad copy for a summer sale campaign' },
  { icon: FileText, label: 'Blog outline', prompt: 'Create a blog post outline about digital marketing trends' },
  { icon: TrendingUp, label: 'Marketing strategy', prompt: 'Suggest a 30-day marketing strategy for a new product launch' },
  { icon: Lightbulb, label: 'Content ideas', prompt: 'Generate 10 social media content ideas for a tech startup' },
];

const initialMessages = [
  {
    id: '1',
    role: 'assistant',
    content: "Hello! I'm your AI Marketing Assistant. I can help you with:\n\n• Writing compelling ad copy and social media posts\n• Generating content ideas and strategies\n• Analyzing marketing trends and data\n• Creating email subject lines and CTAs\n• Brainstorming campaign concepts\n\nHow can I help you today?",
    timestamp: new Date().toISOString()
  }
];

export default function AIAssistant() {
  const { token } = useAuth();
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/api/marketing/ai-assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: userMessage.content })
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Fallback response if API fails
        const fallbackMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: generateFallbackResponse(userMessage.content),
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, fallbackMessage]);
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      // Provide helpful fallback
      const fallbackMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: generateFallbackResponse(userMessage.content),
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateFallbackResponse = (query) => {
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('ad copy') || lowerQuery.includes('advertisement')) {
      return "Here's a compelling ad copy framework:\n\n**Headline:** [Benefit-driven statement]\n**Body:** Problem → Solution → Proof → CTA\n\n**Example:**\n🎯 \"Stop Wasting Time on Marketing That Doesn't Work\"\n\nTired of campaigns that don't convert? Our AI-powered platform has helped 10,000+ businesses increase ROI by 3x.\n\n✅ Smart automation\n✅ Real-time analytics\n✅ 24/7 optimization\n\n👉 Start your free trial today!";
    }
    
    if (lowerQuery.includes('content') || lowerQuery.includes('ideas') || lowerQuery.includes('social')) {
      return "Here are 5 content ideas for your brand:\n\n1. **Behind-the-Scenes** - Show your team's daily workflow\n2. **Customer Spotlight** - Feature a success story\n3. **Tips & Tricks** - Share industry best practices\n4. **Trending Topics** - Create timely, relevant content\n5. **Interactive Poll** - Engage your audience with questions\n\nWant me to expand on any of these?";
    }
    
    if (lowerQuery.includes('strategy') || lowerQuery.includes('plan')) {
      return "Here's a 30-day marketing strategy framework:\n\n**Week 1: Foundation**\n- Audit current channels\n- Define KPIs and goals\n- Identify target audience\n\n**Week 2: Content Creation**\n- Develop content calendar\n- Create evergreen content\n- Plan promotional campaigns\n\n**Week 3: Execution**\n- Launch campaigns\n- Monitor performance\n- A/B test creatives\n\n**Week 4: Optimization**\n- Analyze results\n- Scale winning campaigns\n- Plan next month\n\nWould you like more details on any phase?";
    }
    
    return "I'd be happy to help with that! Here are some suggestions:\n\n1. **Be specific** - Tell me your industry, target audience, and goals\n2. **Share context** - What platforms are you focusing on?\n3. **Set constraints** - Any budget, timeline, or brand guidelines?\n\nThe more details you provide, the better I can assist you. What would you like to explore?";
  };

  const handleQuickPrompt = (prompt) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  const copyToClipboard = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const regenerateResponse = async (messageId) => {
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex > 0) {
      const previousUserMessage = messages[messageIndex - 1];
      if (previousUserMessage.role === 'user') {
        setInput(previousUserMessage.content);
        setMessages(messages.slice(0, messageIndex - 1));
      }
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary" />
            AI Assistant
          </h1>
          <p className="text-muted-foreground">Your intelligent marketing companion</p>
        </div>
        <button 
          onClick={() => setMessages(initialMessages)}
          className="marketing-btn-secondary flex items-center gap-2"
          data-testid="new-chat-btn"
        >
          <RefreshCw className="w-4 h-4" />
          <span>New Chat</span>
        </button>
      </div>

      {/* Quick Prompts */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {quickPrompts.map((item, index) => (
          <button
            key={index}
            onClick={() => handleQuickPrompt(item.prompt)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-lg text-sm whitespace-nowrap hover:bg-secondary/80 transition-colors"
            data-testid={`quick-prompt-${index}`}
          >
            <item.icon className="w-4 h-4 text-primary" />
            <span>{item.label}</span>
          </button>
        ))}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto marketing-card p-4 space-y-4">
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`message-${message.id}`}
          >
            <div className={`max-w-[80%] ${message.role === 'user' ? 'order-2' : ''}`}>
              <div className={`flex items-start gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  message.role === 'user' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary'
                }`}>
                  {message.role === 'user' ? 'U' : <Bot className="w-4 h-4 text-primary" />}
                </div>
                <div className={`p-4 rounded-xl ${
                  message.role === 'user' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary'
                }`}>
                  <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                </div>
              </div>
              
              {/* Message Actions */}
              {message.role === 'assistant' && message.id !== '1' && (
                <div className="flex items-center gap-1 mt-2 ml-11">
                  <button 
                    onClick={() => copyToClipboard(message.content, message.id)}
                    className="p-1.5 hover:bg-secondary rounded-lg transition-colors"
                  >
                    {copiedId === message.id ? (
                      <Check className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                  <button 
                    onClick={() => regenerateResponse(message.id)}
                    className="p-1.5 hover:bg-secondary rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <button className="p-1.5 hover:bg-secondary rounded-lg transition-colors">
                    <ThumbsUp className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <button className="p-1.5 hover:bg-secondary rounded-lg transition-colors">
                    <ThumbsDown className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                <Bot className="w-4 h-4 text-primary" />
              </div>
              <div className="p-4 rounded-xl bg-secondary">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="mt-4 flex gap-3">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder="Ask anything about marketing..."
          className="marketing-input flex-1"
          disabled={isLoading}
          data-testid="ai-input"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          className="marketing-btn-primary px-6 disabled:opacity-50"
          data-testid="send-btn"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
